import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';

import { CurrentContact } from '@/redux/modules/types/contacts';

import useReconcileAccountRiskProfile from '@/hooks/planning/useReconcileAccountRiskProfile';

import { useDeletePlanAccountMutation, useClearPlanContactMutation, planningCacheUtils } from '@/redux/api/planning';

import { formatMessage } from '@/utils/i18n';
import { getEmptyPortfolio } from '@/utils/portfolios';
import notification from '@/utils/notification';

import { Account, Plan } from '@/types/planning';
import Portfolio from '@/types/portfolio';
import { DispatchProps } from '../ContactPlanAccounts';
import { Dispatch } from '@/redux';

type UseActionsProps = {
  currentContact: CurrentContact;
  dispatchProps: DispatchProps;
  plan?: Plan;
  accounts: Account[];
  i18nBasePath: string;
};

export const useActions = ({ currentContact, dispatchProps, plan, accounts, i18nBasePath }: UseActionsProps) => {
  const { attachProfileToPortfolio, loadPortfolioForEdit, logNewPortfolio, resetPortfolio, setPortfolioContact, setPortfolioPlanAccount } = dispatchProps;
  const history = useHistory();
  const dispatch = useDispatch<Dispatch>();
  const { getAccountRiskProfile } = useReconcileAccountRiskProfile();

  const [deletePlanAccount] = useDeletePlanAccountMutation();
  const [clearPlanContact] = useClearPlanContactMutation();

  const updatePortfolioCount = () => {
    if (!plan?._id) return;
    dispatch(planningCacheUtils.refreshPlan(plan._id));
  };

  const onCreatePortfolio = (planAccountId: string) => {
    const contact = currentContact?.contact;
    if (!contact) {
      return;
    }

    resetPortfolio();

    const account = plan?.accounts.find(acc => acc._id === planAccountId);
    const newPortfolio = getEmptyPortfolio();
    newPortfolio.size = account?.totalValue;

    loadPortfolioForEdit(newPortfolio);
    setPortfolioContact(contact);
    setPortfolioPlanAccount(plan!._id!, planAccountId, account);

    const riskProfile = getAccountRiskProfile(account);
    if (riskProfile) {
      attachProfileToPortfolio(riskProfile);
    }
    logNewPortfolio();
  };

  const onGoToImportAccounts = () => {
    history.push(`/planning/${plan?._id}/import-accounts`);
  };

  const onRemovePlanAccount = async (selectedPlanAccount?: Account) => {
    const hasValidIds = !!(plan?._id && selectedPlanAccount?._id);
    const hasAccounts = accounts.length > 0;

    if (!hasValidIds || !hasAccounts) {
      return;
    }

    const contactId = currentContact?.contact?._id;

    try {
      await deletePlanAccount({
        planId: plan._id!,
        accountId: selectedPlanAccount._id!,
        contactId,
      }).unwrap();
      notification.success(formatMessage(`${i18nBasePath}.Notifications.Delete.Success`));
    } catch (error) {
      notification.error(formatMessage(`${i18nBasePath}.Notifications.Delete.Failed`));
    }
  };

  const onUnlinkPlan = async (planId: string) => {
    try {
      await clearPlanContact({ planId, contactId: currentContact?.contact?._id }).unwrap();
      notification.success(formatMessage(`${i18nBasePath}.Notifications.UnlinkContact.Success`));
    } catch (error) {
      notification.error(formatMessage(`${i18nBasePath}.Notifications.UnlinkContact.Failed`));
    }
  };

  const onSavePortfolio = async (portfolio: Portfolio): Promise<Portfolio> => {
    const planAccountId = portfolio.planning?.planAccountId;
    if (planAccountId) {
      updatePortfolioCount();
    }
    return Promise.resolve(portfolio);
  };

  return {
    onCreatePortfolio,
    onGoToImportAccounts,
    onRemovePlanAccount,
    onSavePortfolio,
    onUnlinkPlan,
  };
};
